/** Phase 4.1.0 — Autopilot policy editor + recent runs.
 *
 * One page with two cards:
 *   • Policy card — top — full editor (mode + optimization_target + budget/platform/content/schedule).
 *     Analyst (and any read-only role) sees the same form with all inputs disabled.
 *   • Recent runs card — bottom — last N runs with a "Run now" trigger (manager+).
 *
 * "Convert to approval request" affordance on pending-mode actions is a DISABLED button
 * with a tooltip — there is no backend bridge for it yet (documented known gap).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import AutopilotSafetyControls from "./AutopilotSafetyControls";
import { Check } from "lucide-react";

const MODES = ["off", "prepare_only", "with_approval", "auto_within_frame"];
const TARGETS = ["cpl_min", "roas_max", "leads_max", "balanced"];
const PLATFORMS = ["meta", "google_search", "linkedin", "tiktok", "x", "youtube"];
const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function canWritePolicy(role) {
  return ["owner", "admin", "manager"].includes(role);
}
function canExecute(role) {
  return ["owner", "admin", "manager"].includes(role);
}

function ChipMulti({ options, value, onChange, disabled, testid }) {
  const set = new Set(value || []);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((opt) => {
        const on = set.has(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            data-testid={`${testid}-${opt}`}
            data-on={on ? "1" : "0"}
            onClick={() => {
              const next = new Set(set);
              if (on) next.delete(opt); else next.add(opt);
              onChange(Array.from(next));
            }}
            className={[
              "px-2.5 py-1 rounded-full text-xs border transition-colors",
              on
                ? "bg-foreground text-background border-foreground"
                : "bg-background text-muted-foreground border-input hover:bg-accent",
              disabled ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const tone = status === "completed" ? "default" : status === "failed" ? "destructive" : "secondary";
  return <Badge variant={tone} data-testid={`run-status-${status}`}>{status}</Badge>;
}

function RelativeTime({ iso }) {
  if (!iso) return <span className="text-muted-foreground">—</span>;
  const d = new Date(iso);
  return <span title={d.toLocaleString()}>{d.toLocaleString()}</span>;
}

export default function Autopilot() {
  const t = useT();
  const nav = useNavigate();
  const me = auth.getUser() || { role: "" };
  const writable = canWritePolicy(me.role);
  const executable = canExecute(me.role);

  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [runs, setRuns] = useState([]);
  const [runningNow, setRunningNow] = useState(false);

  const dirty = useMemo(() => {
    if (!policy || !draft) return false;
    return JSON.stringify(policy) !== JSON.stringify(draft);
  }, [policy, draft]);

  async function loadPolicy() {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/autopilot/policy", { skipPermissionToast: true });
      setPolicy(r.data); setDraft(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setError(t("autopilot.errors.no_policy"));
      else setError(t("autopilot.errors.load_failed"));
    } finally { setLoading(false); }
  }
  async function loadRuns() {
    try {
      const r = await api.get("/autopilot/runs", { params: { limit: 20 }, skipPermissionToast: true });
      setRuns(r.data.items || []);
    } catch (e) { /* read-only roles get []; fine */ }
  }
  useEffect(() => { loadPolicy(); loadRuns(); /* eslint-disable-next-line */ }, []);

  async function save() {
    if (!draft) return;
    setSaving(true); setError(null);
    try {
      const body = {
        mode: draft.mode,
        optimization_target: draft.optimization_target,
        budget_rules: draft.budget_rules,
        platform_rules: draft.platform_rules,
        campaign_type_rules: draft.campaign_type_rules,
        content_rules: draft.content_rules,
        schedule: draft.schedule,
        active: draft.active,
        change_summary: `ui:${draft.mode}/${draft.optimization_target}`,
      };
      const r = await api.put("/autopilot/policy", body);
      setPolicy(r.data); setDraft(r.data);
      toast.success(t("autopilot.policy.saved"));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.error || t("autopilot.errors.save_failed")));
    } finally { setSaving(false); }
  }

  async function runNow() {
    setRunningNow(true);
    try {
      const r = await api.post("/autopilot/run", { use_optimization_agent: true });
      toast.success(t("autopilot.run.triggered"));
      // Optimistic: prepend the new run, then refresh.
      setRuns((prev) => [r.data, ...prev].slice(0, 20));
      await loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || t("autopilot.errors.run_failed"));
    } finally { setRunningNow(false); }
  }

  if (loading) {
    return <div data-testid="autopilot-loading" className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  }

  return (
    <div data-testid="autopilot-page" className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("autopilot.page_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("autopilot.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {policy && (
            <Badge variant="outline" data-testid="autopilot-policy-version" className="font-mono text-[11px]">
              v{policy.version}
            </Badge>
          )}
          <Link to="/app/autopilot/actions" data-testid="autopilot-actions-link">
            <Button variant="outline" size="sm">{t("autopilot.actions.link")}</Button>
          </Link>
        </div>
      </div>

      {/* Master-P3 Wave 7.5 — hard limits, business frame, not-aus. */}
      <AutopilotSafetyControls />

      {error && <p className="text-sm text-rose-600" data-testid="autopilot-error">{error}</p>}

      {/* ----- POLICY CARD ----- */}
      {draft && (
        <Card data-testid="autopilot-policy-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("autopilot.policy.title")}</CardTitle>
            {!writable && (
              <p className="text-xs text-muted-foreground" data-testid="autopilot-policy-readonly">
                {t("autopilot.policy.readonly_notice")}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">

            {/* Mode + Optimization Target — the two anchor selectors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("autopilot.policy.mode_label")}
                </label>
                {draft.mode === "off" && (
                  <div
                    data-testid="autopilot-mode-off-banner"
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  >
                    Automatik ist derzeit deaktiviert. Wähle unten einen Modus, um sie wieder zu aktivieren.
                  </div>
                )}
                <div
                  role="radiogroup"
                  aria-label={t("autopilot.policy.mode_label")}
                  data-testid="autopilot-mode-cards"
                  className="grid grid-cols-1 gap-2"
                >
                  {["prepare_only", "with_approval", "auto_within_frame"].map((m) => {
                    const active = draft.mode === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={!writable}
                        onClick={() => setDraft({ ...draft, mode: m })}
                        data-testid={`autopilot-mode-card-${m}`}
                        data-active={active ? "true" : "false"}
                        className={`text-left rounded-md border px-3 py-2 transition-colors
                                     focus:outline-none focus:ring-2 focus:ring-slate-400
                                     ${active
                                        ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900"
                                        : "border-slate-200 hover:bg-slate-50"}
                                     ${!writable ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            aria-hidden="true"
                            className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border
                                         ${active
                                            ? "border-slate-900 bg-slate-900 text-white"
                                            : "border-slate-300 bg-white"}`}
                          >
                            {active ? (
                              <Check className="h-3 w-3" />
                            ) : null}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium">
                              {t(`autopilot.modes.${m}.label`)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t(`autopilot.modes.${m}.help`)}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {writable && draft.mode !== "off" && (
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, mode: "off" })}
                    data-testid="autopilot-mode-off-btn"
                    className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    Automatik komplett deaktivieren
                  </button>
                )}
                <p className="text-xs text-muted-foreground" data-testid="autopilot-mode-help">
                  {t(`autopilot.modes.${draft.mode}.help`)}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("autopilot.policy.target_label")}
                </label>
                <Select value={draft.optimization_target || "cpl_min"} disabled={!writable}
                  onValueChange={(v) => setDraft({ ...draft, optimization_target: v })}>
                  <SelectTrigger data-testid="autopilot-target-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TARGETS.map((tg) => (
                      <SelectItem key={tg} value={tg} data-testid={`autopilot-target-${tg}`}>
                        {t(`autopilot.targets.${tg}.label`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground" data-testid="autopilot-target-help">
                  {t(`autopilot.targets.${draft.optimization_target || "cpl_min"}.help`)}
                </p>
              </div>
            </div>

            {/* Budget rules */}
            <fieldset className="space-y-3" data-testid="autopilot-budget-fieldset">
              <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("autopilot.policy.budget_rules")}
              </legend>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {[
                  ["max_daily_change_pct", 0.0, 1.0, 0.01],
                  ["max_daily_change_abs", 0, 100000, 1],
                  ["max_monthly_total_abs", 0, 1000000, 10],
                  ["approval_threshold_pct", 0.0, 1.0, 0.01],
                  ["approval_threshold_abs", 0, 100000, 1],
                  ["stop_loss_pct", 0.0, 5.0, 0.05],
                  ["scaling_step_pct", 0.0, 1.0, 0.01],
                ].map(([k, min, max, step]) => (
                  <label key={k} className="space-y-1">
                    <span className="text-xs text-muted-foreground">{t(`autopilot.policy.${k}`)}</span>
                    <Input
                      type="number" min={min} max={max} step={step}
                      disabled={!writable}
                      value={draft.budget_rules?.[k] ?? 0}
                      data-testid={`autopilot-budget-${k}`}
                      onChange={(e) => setDraft({
                        ...draft,
                        budget_rules: { ...draft.budget_rules, [k]: Number(e.target.value) },
                      })}
                    />
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Platform rules */}
            <fieldset className="space-y-2" data-testid="autopilot-platform-fieldset">
              <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("autopilot.policy.platform_rules")}
              </legend>
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">{t("autopilot.policy.allowed_platforms")}</label>
                <ChipMulti
                  options={PLATFORMS}
                  value={draft.platform_rules?.allowed_platforms || []}
                  disabled={!writable}
                  testid="autopilot-allowed-platforms"
                  onChange={(next) => setDraft({
                    ...draft,
                    platform_rules: { ...draft.platform_rules, allowed_platforms: next },
                  })}
                />
              </div>
            </fieldset>

            {/* Content rules */}
            <fieldset className="space-y-3" data-testid="autopilot-content-fieldset">
              <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("autopilot.policy.content_rules")}
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">{t("autopilot.policy.blocked_claims")}</span>
                  <Textarea
                    rows={3}
                    disabled={!writable}
                    data-testid="autopilot-blocked-claims"
                    value={(draft.content_rules?.blocked_claims || []).join("\n")}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: {
                        ...draft.content_rules,
                        blocked_claims: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                      },
                    })}
                  />
                  {writable && policy?.id && (
                    <button
                      type="button"
                      data-testid="autopilot-reset-blocklist-btn"
                      onClick={async () => {
                        if (!window.confirm(
                          "Dies überschreibt Deine benutzerdefinierten "
                          + "Sperrbegriffe mit den DE-Immobilien-Standards. "
                          + "Fortfahren?")) return;
                        try {
                          const r = await api.post(
                            `/autopilot/policies/${policy.id}/reset-blocklist`);
                          setDraft({
                            ...draft,
                            content_rules: {
                              ...draft.content_rules,
                              blocked_claims: r.data.blocked_claims,
                            },
                          });
                          toast.success(
                            "Sperrbegriffe auf DE-Immobilien-Standards zurückgesetzt");
                        } catch (e) {
                          toast.error("Zurücksetzen fehlgeschlagen");
                        }
                      }}
                      className="text-xs underline text-muted-foreground hover:text-foreground"
                    >
                      Auf DE-Immobilien-Standards zurücksetzen
                    </button>
                  )}
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">{t("autopilot.policy.brand_safety_keywords")}</span>
                  <Textarea
                    rows={3}
                    disabled={!writable}
                    data-testid="autopilot-brand-safety-keywords"
                    value={(draft.content_rules?.brand_safety_keywords || []).join("\n")}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: {
                        ...draft.content_rules,
                        brand_safety_keywords: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                      },
                    })}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">{t("autopilot.policy.min_quality_score")}</span>
                  <Input
                    type="number" min={0} max={1} step={0.05}
                    disabled={!writable}
                    data-testid="autopilot-min-quality-score"
                    value={draft.content_rules?.min_quality_score ?? 0.6}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: { ...draft.content_rules, min_quality_score: Number(e.target.value) },
                    })}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">{t("autopilot.policy.min_data_threshold_days")}</span>
                  <Input
                    type="number" min={0} max={90} step={1}
                    disabled={!writable}
                    data-testid="autopilot-min-data-threshold-days"
                    value={draft.content_rules?.min_data_threshold_days ?? 1}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: { ...draft.content_rules, min_data_threshold_days: Number(e.target.value) },
                    })}
                  />
                </label>
                {/* Slice 4b-2a — minimum_data_threshold triplet. */}
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">Mindest-Impressions (min_impressions)</span>
                  <Input
                    type="number" min={0} step={100}
                    disabled={!writable}
                    data-testid="autopilot-min-impressions"
                    placeholder="1000"
                    value={draft.content_rules?.min_impressions ?? 1000}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: { ...draft.content_rules,
                                              min_impressions: Number(e.target.value) },
                    })}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">Mindest-Klicks (min_clicks)</span>
                  <Input
                    type="number" min={0} step={10}
                    disabled={!writable}
                    data-testid="autopilot-min-clicks"
                    placeholder="50"
                    value={draft.content_rules?.min_clicks ?? 50}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: { ...draft.content_rules,
                                              min_clicks: Number(e.target.value) },
                    })}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted-foreground">Mindest-Laufzeit in Tagen (min_days_since_launch)</span>
                  <Input
                    type="number" min={0} max={90} step={1}
                    disabled={!writable}
                    data-testid="autopilot-min-days-since-launch"
                    placeholder="3"
                    value={draft.content_rules?.min_days_since_launch ?? 3}
                    onChange={(e) => setDraft({
                      ...draft,
                      content_rules: { ...draft.content_rules,
                                              min_days_since_launch: Number(e.target.value) },
                    })}
                  />
                </label>
              </div>
            </fieldset>

            {/* Schedule */}
            <fieldset className="space-y-2" data-testid="autopilot-schedule-fieldset">
              <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("autopilot.policy.schedule")}
              </legend>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => {
                  const on = (draft.schedule?.enabled_weekdays || []).includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      disabled={!writable}
                      data-testid={`autopilot-weekday-${d}`}
                      data-on={on ? "1" : "0"}
                      onClick={() => {
                        const cur = new Set(draft.schedule?.enabled_weekdays || []);
                        if (on) cur.delete(d); else cur.add(d);
                        setDraft({
                          ...draft,
                          schedule: { ...draft.schedule, enabled_weekdays: Array.from(cur) },
                        });
                      }}
                      className={[
                        "px-2.5 py-1 rounded-md text-xs border",
                        on ? "bg-foreground text-background border-foreground"
                           : "bg-background text-muted-foreground",
                      ].join(" ")}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Active toggle + save */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!draft.active}
                  disabled={!writable}
                  data-testid="autopilot-active-switch"
                  onCheckedChange={(v) => setDraft({ ...draft, active: !!v })}
                />
                <span className="text-sm">{t("autopilot.policy.active")}</span>
              </div>
              {writable && (
                <Button onClick={save} disabled={!dirty || saving}
                  data-testid="autopilot-save-btn">
                  {saving ? t("autopilot.policy.saving") : t("autopilot.policy.save")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ----- RECENT RUNS CARD ----- */}
      <Card data-testid="autopilot-runs-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t("autopilot.runs.title")}</CardTitle>
          {executable && (
            <Button size="sm" onClick={runNow} disabled={runningNow}
              data-testid="autopilot-run-now-btn">
              {runningNow ? t("autopilot.run.triggering") : t("autopilot.run.run_now")}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="autopilot-runs-empty">
              {t("autopilot.runs.empty")}
            </p>
          ) : (
            <ul className="divide-y" data-testid="autopilot-runs-list">
              {runs.map((r) => (
                <li
                  key={r.id}
                  data-testid={`autopilot-run-row-${r.id}`}
                  className="py-2 grid grid-cols-12 gap-2 items-center text-sm cursor-pointer hover:bg-accent/30 rounded-md px-2"
                  onClick={() => nav(`/app/autopilot/runs/${r.id}`)}
                >
                  <div className="col-span-3"><RelativeTime iso={r.started_at} /></div>
                  <div className="col-span-2 text-xs text-muted-foreground">{r.triggered_by}</div>
                  <div className="col-span-2"><StatusBadge status={r.status} /></div>
                  <div className="col-span-2 text-xs font-mono">
                    {t("autopilot.runs.n_actions", { n: (r.selected_action_ids || []).length })}
                  </div>
                  <div className="col-span-2 text-xs font-mono text-muted-foreground">
                    {r.latency_ms ? `${(r.latency_ms / 1000).toFixed(1)}s` : "—"}
                  </div>
                  <div className="col-span-1 text-right">
                    <Button size="sm" variant="ghost" data-testid={`autopilot-run-open-${r.id}`}>
                      {t("autopilot.runs.open")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ----- Slice 4b-2b: Recommendations feed + Dry-run preview ----- */}
      <RecommendationsFeed />
      <DryRunPreviewCard />
    </div>
  );
}

/* ═══ Slice 4b-2b — Recommendations feed ══════════════════════════════ */

function RecommendationsFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/autopilot/recommendations");
      setItems(r.data.items || []);
    } catch (e) {
      setError(String(e?.response?.data?.detail || e?.message || e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    // Optimistic remove.
    const before = items;
    setItems(items.filter((r) => r.id !== id));
    try {
      await api.post(`/autopilot/recommendations/${id}/dismiss`);
      toast.success("Empfehlung verworfen");
    } catch (e) {
      setItems(before);
      toast.error("Verwerfen fehlgeschlagen");
    }
  };

  return (
    <Card data-testid="autopilot-recommendations-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Empfehlungen</CardTitle>
        <Button size="sm" variant="outline" onClick={load}
                  data-testid="autopilot-recommendations-refresh">
          Aktualisieren
        </Button>
      </CardHeader>
      <CardContent>
        {loading && (
          <p className="text-sm text-muted-foreground"
              data-testid="autopilot-recommendations-loading">
            Lade…
          </p>
        )}
        {error && !loading && (
          <p className="text-sm text-destructive"
              data-testid="autopilot-recommendations-error">
            Fehler: {error}
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-muted-foreground"
              data-testid="autopilot-recommendations-empty">
            Keine offenen Empfehlungen — Autopilot beobachtet die Kampagnen.
          </p>
        )}
        {!loading && !error && items.length > 0 && (
          <ul className="divide-y"
                data-testid="autopilot-recommendations-list">
            {items.map((r) => (
              <li key={r.id}
                    data-testid={`autopilot-recommendation-row-${r.id}`}
                    className="py-2 flex items-start justify-between gap-3">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="secondary"
                              data-testid={`autopilot-rec-rule-${r.id}`}>
                      {r.rule_type}
                    </Badge>
                    {r.severity && (
                      <Badge variant="outline">{r.severity}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {r.campaign_id}
                    </span>
                  </div>
                  <div className="text-sm">{r.rationale}</div>
                  <div className="text-xs text-muted-foreground">
                    Vorschlag: {r.suggested_action}
                  </div>
                </div>
                <Button size="sm" variant="ghost"
                          data-testid={`autopilot-rec-dismiss-${r.id}`}
                          onClick={() => dismiss(r.id)}>
                  Verwerfen
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══ Slice 4b-2b — Dry-run preview ═══════════════════════════════════ */

function DryRunPreviewCard() {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/campaigns");
        const items = (r.data.items || r.data || [])
          .slice(0, 25)
          .map((c) => ({ id: c.id, name: c.name }));
        setCampaigns(items);
        if (items.length) setSelected(items[0].id);
      } catch (_e) {
        setCampaigns([]);
      }
    })();
  }, []);

  const runDry = async () => {
    if (!selected) return;
    setRunning(true); setResult(null);
    try {
      const r = await api.post("/autopilot/dry-run",
                                              { campaign_id: selected });
      setResult(r.data);
    } catch (e) {
      toast.error("Dry-Run fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card data-testid="autopilot-dry-run-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Regel-Vorschau</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <label className="flex-1 space-y-1">
            <span className="text-xs text-muted-foreground">Kampagne</span>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={selected}
              data-testid="autopilot-dry-run-campaign-select"
              onChange={(e) => setSelected(e.target.value)}
            >
              {campaigns.length === 0 && (
                <option value="">Keine Kampagnen verfügbar</option>
              )}
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <Button
            onClick={runDry}
            disabled={running || !selected}
            data-testid="autopilot-dry-run-btn"
          >
            {running ? "Prüfe…" : "Regeln evaluieren"}
          </Button>
        </div>
        {result && (
          <div className="text-sm space-y-2"
                 data-testid="autopilot-dry-run-result">
            <div className="text-xs text-muted-foreground">
              Creative-Text-Scan: {" "}
              {result.creative_text_scan?.blocked_claim_hit
                ? <span data-testid="autopilot-dry-run-claim-hit">
                      Sperrbegriff: <strong>
                        {result.creative_text_scan.blocked_claim_hit}
                      </strong>
                    </span>
                : "OK"}
              {result.creative_text_scan?.brand_safety_hit && (
                <> · Brand-Safety: <strong>
                    {result.creative_text_scan.brand_safety_hit}
                  </strong></>
              )}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1">Aktion</th>
                  <th>Ergebnis</th>
                  <th>Blockierende Regeln</th>
                </tr>
              </thead>
              <tbody>
                {(result.per_action || []).map((pa) => (
                  <tr key={pa.action_type}
                        data-testid={`autopilot-dry-run-row-${pa.action_type}`}
                        className="border-t">
                    <td className="py-1 pr-2">{pa.action_type}</td>
                    <td className="py-1 pr-2">
                      {pa.would_block
                        ? <Badge variant="destructive">blockiert</Badge>
                        : <Badge variant="secondary">frei</Badge>}
                    </td>
                    <td className="py-1 text-muted-foreground">
                      {(pa.blocking_rules || []).join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
